/**
 * The agent has stopped and is asking permission — human-in-the-loop review.
 *
 * This is the one place in chat where the model is about to *change the user's
 * model file*, so it is deliberately not a toast and not a dialog that can be
 * dismissed by clicking away. It sits in the transcript, above the composer,
 * and the turn does not continue until it is answered.
 *
 * `ask_user` is the exception: the agent is asking a question, not permission,
 * so the answer is typed rather than approved. It is detected by the action
 * name, exactly as the web client detects it.
 *
 * **`edit` is not offered yet.** `review_configs` can allow it — editing the
 * tool's arguments before they run — and rendering an editor per tool schema is
 * its own piece of work. Approve and reject are the two that make the feature
 * safe; offering a third that silently approves unchanged args would be worse
 * than not offering it.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { composeAskAnswer, describeAskUser, toggleAskOption } from "../../lib/askUser";
import { describeApproval } from "../../lib/hitlCard";
import { Markdown } from "./Markdown";
import type { ActionRequest, InterruptPayload, ResumeDecision } from "../../lib/chat";

/**
 * Plan mode's review step — the old web's plan card (`Message.tsx`, the
 * `isSubmitPlan` branch), structure and wording kept (owner, 2026-08-25):
 *
 *   - a blue header strip with the clipboard glyph and "Review plan";
 *   - the plan's **title** as a heading (not editable — the agent named it);
 *   - the plan as rendered **markdown** in a scrolling grey box, swapped for
 *     a monospace textarea by the "Edit plan" toggle;
 *   - a feedback box, then **Request changes** full width — enabled only when
 *     the plan was edited or a comment written, because a revise with nothing
 *     to revise is a no-op the agent cannot act on;
 *   - **Execute** (dark) and **Reject** (outline) side by side.
 *
 * A written comment **blocks Execute**: a comment can only be honoured by
 * re-planning, so approving with one pending would silently drop it.
 */
function PlanReview({
  action,
  busy,
  onRespond,
}: {
  action: ActionRequest;
  busy: boolean;
  onRespond: (decision: ResumeDecision) => void;
}) {
  const { t } = useLocale();
  const planText = typeof action.args?.["plan"] === "string" ? action.args["plan"] : "";
  const planTitle =
    typeof action.args?.["title"] === "string" && action.args["title"].trim()
      ? action.args["title"].trim()
      : t("hitl.planLabel");
  const [editing, setEditing] = useState(false);
  const [editedPlan, setEditedPlan] = useState(planText);
  const [comment, setComment] = useState("");

  // A re-plan arrives as a new interrupt with new text; the boxes must follow it
  // rather than keep showing the plan that was just revised away.
  useEffect(() => {
    setEditedPlan(planText);
    setComment("");
    setEditing(false);
  }, [planText]);

  const planChanged = editedPlan.trim() !== planText.trim();
  const hasComment = comment.trim().length > 0;
  const hasFeedback = planChanged || hasComment;

  return (
    <section className="plan-card" aria-live="polite">
      <header className="plan-card-head">
        <ClipboardIcon />
        <span className="plan-card-head-text">{t("hitl.planReview")}</span>
      </header>

      <div className="plan-card-body">
        <div className="plan-card-title">{planTitle}</div>

        {editing ? (
          <textarea
            className="plan-editor"
            value={editedPlan}
            disabled={busy}
            aria-label={t("hitl.editPlan")}
            onChange={(e) => setEditedPlan(e.target.value)}
          />
        ) : (
          <div className="plan-preview">
            <Markdown text={editedPlan || t("hitl.planLabel")} />
          </div>
        )}

        <button type="button" className="plan-edit-toggle" disabled={busy} onClick={() => setEditing((v) => !v)}>
          <PencilIcon />
          {t("hitl.editPlan")}
        </button>

        <div className="plan-feedback">
          <textarea
            className="plan-feedback-input"
            value={comment}
            disabled={busy}
            placeholder={t("hitl.planFeedbackPlaceholder")}
            aria-label={t("hitl.planFeedbackPlaceholder")}
            onChange={(e) => setComment(e.target.value)}
          />
          <button
            type="button"
            className="plan-btn is-request"
            disabled={busy || !hasFeedback}
            onClick={() =>
              onRespond({
                approved: false,
                intent: "revise",
                ...(planChanged ? { edited_plan: editedPlan } : {}),
                ...(hasComment ? { decision_reason: comment.trim() } : {}),
              })
            }
          >
            <PencilIcon />
            {t("hitl.requestChanges")}
          </button>
        </div>

        <div className="plan-actions">
          <button
            type="button"
            className="plan-btn is-execute"
            disabled={busy || hasComment}
            title={hasComment ? t("hitl.approveBlockedByComment") : undefined}
            onClick={() =>
              onRespond(
                planChanged
                  ? { approved: true, edited_plan: editedPlan, edited_title: planTitle }
                  : { approved: true },
              )
            }
          >
            <CheckIcon />
            {t("hitl.execute")}
          </button>
          <button
            type="button"
            className="plan-btn is-reject"
            disabled={busy}
            onClick={() => onRespond({ approved: false, intent: "reject" })}
          >
            <XCircleIcon />
            {t("hitl.reject")}
          </button>
        </div>
      </div>
    </section>
  );
}

/** lucide `ClipboardList` — the old card's glyph. */
function ClipboardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M12 11h4" />
      <path d="M12 16h4" />
      <path d="M8 11h.01" />
      <path d="M8 16h.01" />
    </svg>
  );
}

/** lucide `Pencil`. */
function PencilIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

/** lucide `Check`. */
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** lucide `XCircle`. */
function XCircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </svg>
  );
}

/**
 * Plan mode's clarifying question (#99): the question from `args.question`, the agent's
 * preset choices as selectable rows, single- or multi-select per `allowMultiple`, and a
 * free-text "other" that is always offered. The answer sent back is the selected choices
 * followed by the free text — the web client's contract (`Message.tsx`, `isAskUser`), so
 * the agent sees the same `resume.answer` from either renderer.
 *
 * The middleware's `description` is deliberately not the question: it is the tool's
 * boilerplate plus `Tool: ask_user` and the serialized `Args`, which is what the first card
 * showed the user. `describeAskUser` keeps that text out.
 */
function AskUserCard({
  action,
  busy,
  onRespond,
}: {
  action: ActionRequest;
  busy: boolean;
  onRespond: (decision: ResumeDecision) => void;
}) {
  const { t } = useLocale();
  const prompt = describeAskUser(action);
  const [selected, setSelected] = useState<string[]>([]);
  const [other, setOther] = useState("");

  // A new question arrives as a new interrupt; a selection made for the last one must not
  // carry over into it.
  useEffect(() => {
    setSelected([]);
    setOther("");
  }, [prompt.question, prompt.options.join(" ")]);

  const answer = composeAskAnswer(selected, other);
  const canSubmit = !busy && answer.length > 0;
  const submit = () => {
    if (canSubmit) onRespond({ approved: true, answer });
  };

  return (
    <section className="plan-card" aria-live="polite">
      <header className="plan-card-head">
        <QuestionIcon />
        <span className="plan-card-head-text">{t("hitl.askUserTitle")}</span>
      </header>

      <div className="plan-card-body">
        <div className="plan-card-title">{prompt.question || t("hitl.askUserTitle")}</div>

        {prompt.options.length > 0 ? (
          <div
            className="ask-options"
            role={prompt.allowMultiple ? "group" : "radiogroup"}
            aria-label={prompt.question || t("hitl.askUserTitle")}
          >
            {prompt.options.map((option) => {
              const checked = selected.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  role={prompt.allowMultiple ? "checkbox" : "radio"}
                  aria-checked={checked}
                  className={`ask-option${checked ? " is-checked" : ""}${prompt.allowMultiple ? " is-multi" : ""}`}
                  disabled={busy}
                  onClick={() => setSelected((prev) => toggleAskOption(prev, option, prompt.allowMultiple))}
                >
                  <span className="ask-option-mark" aria-hidden="true">
                    {checked ? <CheckIcon /> : null}
                  </span>
                  <span className="ask-option-text">{option}</span>
                </button>
              );
            })}
          </div>
        ) : null}

        <input
          type="text"
          className="ask-other-input"
          value={other}
          placeholder={t("hitl.askUserOtherPlaceholder")}
          aria-label={t("hitl.askUserOtherPlaceholder")}
          disabled={busy}
          onChange={(e) => setOther(e.target.value)}
          onKeyDown={(e) => {
            // An input method's confirming Enter is not an answer (see SkillEditorChat).
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === "Enter" && canSubmit) {
              e.preventDefault();
              submit();
            }
          }}
        />

        <div className="plan-actions">
          <button type="button" className="plan-btn is-answer" disabled={!canSubmit} onClick={submit}>
            <CheckIcon />
            {t("hitl.askUserSubmit")}
          </button>
          <button
            type="button"
            className="plan-btn is-reject"
            disabled={busy}
            title={t("hitl.askUserCancelHint")}
            onClick={() => onRespond({ approved: false, intent: "reject" })}
          >
            <XCircleIcon />
            {t("hitl.askUserCancel")}
          </button>
        </div>
      </div>
    </section>
  );
}

/** lucide `MessageCircleQuestion` — the web card's glyph. */
function QuestionIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/**
 * One pending action, described rather than dumped.
 *
 * The kicker/title/description split is `HitlApprovalSection`'s and it is what
 * makes the card readable at a glance: the kicker says what *kind* of thing is
 * about to happen, the title says which one, and the description is the agent's
 * own sentence about why. See `lib/hitlCard.ts` for why the raw arguments are
 * deliberately not here.
 */
function ApprovalAction({ action }: { action: ActionRequest }) {
  const { t } = useLocale();
  const card = describeApproval(action);

  return (
    <div className="approval-action">
      {card.kickerKey ? <div className="approval-kicker">{t(card.kickerKey)}</div> : null}
      <div className="approval-title">{card.title || t(card.fallbackKey)}</div>
      {card.description ? <p className="approval-desc">{card.description}</p> : null}
      {card.params ? <div className="approval-params">{card.params}</div> : null}
    </div>
  );
}
export function InterruptPanel({
  interrupt,
  busy,
  onRespond,
}: {
  interrupt: InterruptPayload;
  busy: boolean;
  onRespond: (decision: ResumeDecision) => void;
}) {
  const { t } = useLocale();

  const actions = interrupt.action_requests;
  const askAction = actions.length > 0 && actions.every((a) => a.name === "ask_user") ? actions[0] : undefined;
  const planAction = actions.find((a) => a.name === "submit_plan");

  if (planAction) {
    return <PlanReview action={planAction} busy={busy} onRespond={onRespond} />;
  }

  if (askAction) {
    return <AskUserCard action={askAction} busy={busy} onRespond={onRespond} />;
  }

  return (
    <section className="approval" aria-live="polite">
      {/* The reference's amber header with a pulsing dot. Amber rather than red:
          this is a request, not a failure, and the pulse is the only motion —
          it is the one thing on screen waiting for the user. */}
      <header className="approval-head">
        <span className="approval-pulse" aria-hidden="true">
          <i />
          <i />
        </span>
        <span className="approval-head-text">{t("hitl.approvalRequired")}</span>
      </header>

      <div className="approval-body">
        {actions.map((action, i) => (
          <ApprovalAction key={`${action.name}-${i}`} action={action} />
        ))}

        <div className="approval-buttons">
          <button
            type="button"
            className="approval-btn is-approve"
            disabled={busy}
            onClick={() => onRespond({ approved: true })}
          >
            <CheckIcon />
            {t("hitl.approve")}
          </button>
          <button
            type="button"
            className="approval-btn"
            disabled={busy}
            onClick={() => onRespond({ approved: false })}
          >
            <XCircleIcon />
            {t("hitl.reject")}
          </button>
        </div>
      </div>
    </section>
  );
}
